define('models/config/SchemaModel',[
    'lib/knockout',
    'lib/lodash4',
    'util/networkUtils'
], function (ko, _, networkUtils) {
    'use strict';

    var anyConditionalFromPattern = function(pattern, value) {
        return String(value).match(new RegExp(pattern));
    };

    var FORMAT_VALIDATORS = {
        'ipv4': networkUtils.isIpv4OrIpv4Port,
        'ipv6': networkUtils.isIpv6OrIpv6Port,
        'host-name': networkUtils.isHostname,
        'uri': networkUtils.isUri,
        'integer': _.partial(anyConditionalFromPattern, '^[0-9]*$')
    };

    // Accumulates regular expression patterns to validate on the property.
    // There is a LOT of ways these can be specified in JSON Schema.
    var processPatterns = function (propertyData) {
        var patterns = [];

        if (propertyData.type === 'integer') {
            patterns.push(FORMAT_VALIDATORS.integer);
        }

        if (propertyData.pattern !== undefined) {
            patterns.push(_.partial(anyConditionalFromPattern, propertyData.pattern));
        }

        if (propertyData.format && _.has(FORMAT_VALIDATORS, propertyData.format)) {
            patterns.push(FORMAT_VALIDATORS[propertyData.format]);
        }

        if (propertyData.oneOf) {
            _.each(propertyData.oneOf, function (oneOfItem) {
                if (oneOfItem.format && _.has(FORMAT_VALIDATORS, oneOfItem.format)) {
                    patterns.push(FORMAT_VALIDATORS[oneOfItem.format]);
                }
            });
        }

        if (patterns.length > 0) {
            return patterns;
        }
    };

    // Retrieves the Knockout-Validation options for a schema property.
    var getKnockoutValidationOptions = function (propertyData, propertyName, schema) {
        var validationOptions = {};
        var patterns;

        // Validators for a numeric.
        if (propertyData.type === 'integer' || propertyData.type === 'number') {
            if (schema && _.includes(schema.required, propertyName)) {
                validationOptions.required = true;
            }
            validationOptions.isInteger = {};
            if (propertyData.minimum !== undefined) {
                validationOptions.min = propertyData.minimum;
            }
            if (propertyData.maximum !== undefined) {
                validationOptions.max = propertyData.maximum;
            }

            patterns = processPatterns(propertyData);
            if (patterns) {
                validationOptions.anyConditional = patterns;
            }
        }

        // Validators for a string.
        if (propertyData.type === 'string') {
            if (schema && _.includes(schema.required, propertyName)) {
                validationOptions.required = true;
            }
            if (propertyData.minLength !== undefined) {
                validationOptions.minLength = propertyData.minLength;
            }
            if (propertyData.maxLength !== undefined) {
                validationOptions.maxLength = propertyData.maxLength;
            }

            patterns = processPatterns(propertyData);
            if (patterns) {
                validationOptions.anyConditional = patterns;
            }
        }

        // Validators for array.
        if (propertyData.type === 'array') {
            if (propertyData.minItems !== undefined) {
                validationOptions.minItems = propertyData.minItems;
            }
            if (propertyData.maxItems !== undefined) {
                validationOptions.maxItems = propertyData.maxItems;
            }
        }

        return validationOptions;
    };

    // Parse the merged result of the json schema and partial data map into single complete model.
    var buildModelPropertiesRecursive = function (model, schema, data) {
        _.each(schema.properties, function (propertyData, propertyName) {

            // Objects in the schema are a sub-schema containing their own property definitions.
            if (propertyData.type === "object") {
                model[propertyName] = ko.observable({});
                model[propertyName].schema = propertyData;
                if (data === undefined) {
                    buildModelPropertiesRecursive(model[propertyName](), propertyData);
                }
                else {
                    buildModelPropertiesRecursive(model[propertyName](), propertyData, data[propertyName]);
                }
            }
            else {

                // Get an appropriate observable constructor for the property type.
                var ctor = ko.observable;
                if (propertyData.type === 'array') {
                    ctor = ko.observableArray;
                }

                // If an explicit value for this schema property exists, use it.
                if (data && data[propertyName] !== undefined) {
                    model[propertyName] = ctor(data[propertyName]);
                }
                // If the schema has a default value, use it.
                else if (propertyData.default !== undefined) {
                    model[propertyName] = ctor(propertyData.default);
                }
                // Otherwise set an appropriate 'empty' value for the property type.
                else {
                    if (propertyData.type === 'boolean') {
                        model[propertyName] = ctor(false);
                    }
                    else if (propertyData.type === 'number' || propertyData.type === 'integer') {
                        model[propertyName] = ctor(0);
                    }
                    else if (propertyData.type === 'string') {
                        model[propertyName] = ctor('');
                    }
                    else {
                        model[propertyName] = ctor();
                    }
                }

                // Attach the schema property metadata to the actual property for future reference. This can
                // include information like the data type, a description or an enumeration of available values that
                // are needed later by the ViewModel.
                model[propertyName].schema = propertyData;

                // Attach array item validation options. These can be used later to validate new items.
                if (propertyData.items) {
                    model[propertyName].schema.itemValidationOptions = getKnockoutValidationOptions(propertyData.items);
                }

                model[propertyName].extend(getKnockoutValidationOptions(propertyData, propertyName, schema), { deep: true });
            }
        });
    };

    // Recursively unwraps the model into a plain JSON object.
    var unwrapRecursive = function (source, destination) {
        _.each(source, function (propertyValueAccessor, propertyName) {
            var value = ko.unwrap(propertyValueAccessor);
            if (value instanceof Array) {
                destination[propertyName] = value;
            }
            else if (value instanceof Object) {
                if (typeof(value) !== 'function') {
                    destination[propertyName] = {};
                    unwrapRecursive(value, destination[propertyName]);
                }
            }
            else {
                destination[propertyName] = value;
            }
        });
    };

    /**
     * The SchemaModel constructor.
     */
    var SchemaModel = function (schema, data) {
        var self = this;
        var errors;

        // Handle constructor errors.
        if (!schema) {
            throw 'SchemaModel must be instantiated with a valid JSON schema.';
        }

        /**
         * Returns an array of all errors.
         */
        self.errors = function () {
            return errors();
        };

        /**
         * Checks if the ViewModel is valid.
         */
        self.isValid = function () {
            return errors().length === 0;
        };

        /**
         * Unwraps the model converting it to a plain old JSON object.
         */
        self.unwrap = function () {
            var model = {};
            unwrapRecursive(self, model);

            // We want to make sure that properties that were on the original data, weren't removed. Even if they
            // weren't part of the schema. This is because the schema might be filtered to a behavior, but the UI still
            // needs to be tolerant of other processes modifying the data and not arbitrarily strip out their changes.
            _.merge(model, data, function (modelPropertyValue) {
                return modelPropertyValue;
            });

            return model;
        };

        // Initialize the model properties.
        var model = {};
        buildModelPropertiesRecursive(model, schema, data);
        _.assign(self, model);
        errors = ko.validation.group(model, { deep: true });
        errors.showAllMessages();
    };

    return SchemaModel;
});
